/**
 * Quick Fix Actions — inline warning resolution buttons for
 * Ranking Diagnostics. Each warning type maps to 1-2 actionable
 * buttons that perform the fix without leaving the page.
 */
import React, { useState } from 'react';
import {
  Activity, AlertTriangle, Calculator, CheckCircle2, Edit3, ExternalLink,
  Grid3x3, Loader2, PauseCircle, PlayCircle, RefreshCw, Settings, X,
  Zap,
} from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const API = getBackendUrl();

// ── Warning → Quick Fix mapping ──────────────────────────────
// Match by substring of warning text since warnings are localized Greek strings
const WARNING_ACTIONS = {
  'Stats endpoint stale': ['retest'],
  'Estimated TLH χωρίς raw': ['edit_urls', 'test_url'],
  'Χωρίς listeners & χωρίς plays': ['open_stream', 'toggle_active'],
  'Activity-only fallback': ['test_url', 'edit_urls'],
  'Χαμηλή κάλυψη δεδομένων': ['show_samples'],
  'Μεγάλη απόκλιση raw': ['explain_math'],
};

const getActionsForWarning = (warningText) => {
  for (const [key, actions] of Object.entries(WARNING_ACTIONS)) {
    if (warningText.includes(key)) return actions;
  }
  return [];
};

// ── Individual action renderers ──────────────────────────────
const ActionButton = ({ onClick, icon: Icon, label, busy, variant = 'default', testId }) => {
  const variants = {
    default: 'bg-white/5 hover:bg-white/10 text-gray-200 border-white/10',
    primary: 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 border-blue-500/40',
    danger:  'bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border-rose-500/40',
    success: 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border-emerald-500/40',
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border transition-colors disabled:opacity-50 ${variants[variant]}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
};

// ── Modal shell ──────────────────────────────────────────────
const Modal = ({ onClose, title, children, width = 'max-w-2xl' }) => (
  <div
    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="quickfix-modal"
  >
    <div
      className={`bg-gray-900 border border-white/10 rounded-lg w-full ${width} max-h-[85vh] overflow-hidden flex flex-col shadow-2xl`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/30">
        <div className="font-semibold text-gray-200 text-sm">{title}</div>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-200 p-1 rounded hover:bg-white/5">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-y-auto flex-1 p-4">{children}</div>
    </div>
  </div>
);

// ── Edit URLs modal ──────────────────────────────────────────
const EditUrlsModal = ({ station, onClose, onSaved, token }) => {
  const [statsUrl, setStatsUrl] = useState('');
  const [listenersApiUrl, setListenersApiUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(null); // 'stats' | 'listeners'

  React.useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/stream-monitor/stations/${station.station_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setStatsUrl(d.stats_url || '');
          setListenersApiUrl(d.listeners_api_url || '');
        }
      } finally { setLoading(false); }
    };
    load();
  }, [station.station_id, token]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/stream-monitor/stations/${station.station_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stats_url: statsUrl || null, listeners_api_url: listenersApiUrl || null }),
      });
      if (r.ok) { onSaved?.(); onClose(); }
      else {
        const err = await r.text();
        alert(`Αποτυχία αποθήκευσης: ${err}`);
      }
    } finally { setSaving(false); }
  };

  const testUrl = async (which) => {
    const url = which === 'stats' ? statsUrl : listenersApiUrl;
    if (!url) { alert('Συμπλήρωσε URL πρώτα'); return; }
    setTesting(which);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/api/admin/test-listeners-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url }),
      });
      const d = await r.json();
      setTestResult({ ...d, which });
    } catch (e) {
      setTestResult({ success: false, error: String(e), which });
    } finally { setTesting(null); }
  };

  return (
    <Modal onClose={onClose} title={`Ρυθμίσεις endpoints — ${station.station_name}`}>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Φόρτωση...
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">stats_url (Icecast/Shoutcast status JSON)</label>
            <div className="flex gap-2">
              <input
                data-testid="stats-url-input"
                value={statsUrl}
                onChange={(e) => setStatsUrl(e.target.value)}
                placeholder="https://radio.example.gr/status-json.xsl?mount=/live"
                className="flex-1 bg-gray-800 border border-white/10 rounded px-2 py-1.5 text-xs text-gray-200 font-mono"
              />
              <ActionButton
                onClick={() => testUrl('stats')}
                icon={Zap}
                label="Test"
                busy={testing === 'stats'}
                variant="primary"
                testId="test-stats-url"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">listeners_api_url (portal API returning listener count)</label>
            <div className="flex gap-2">
              <input
                data-testid="listeners-url-input"
                value={listenersApiUrl}
                onChange={(e) => setListenersApiUrl(e.target.value)}
                placeholder="https://portal.example.com/api/listeners"
                className="flex-1 bg-gray-800 border border-white/10 rounded px-2 py-1.5 text-xs text-gray-200 font-mono"
              />
              <ActionButton
                onClick={() => testUrl('listeners')}
                icon={Zap}
                label="Test"
                busy={testing === 'listeners'}
                variant="primary"
                testId="test-listeners-url"
              />
            </div>
          </div>

          {testResult && (
            <div className={`rounded border p-3 text-xs ${
              testResult.success
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-200'
            }`}>
              <div className="font-semibold flex items-center gap-2 mb-1">
                {testResult.success ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                {testResult.success ? 'Επιτυχία' : 'Αποτυχία'}
                <span className="text-gray-400 font-normal">(test {testResult.which} URL)</span>
              </div>
              <div>Listeners detected: <span className="font-mono">{testResult.listeners_detected ?? '—'}</span></div>
              {testResult.raw_status_code && <div>HTTP status: <span className="font-mono">{testResult.raw_status_code}</span></div>}
              {testResult.error && <div className="text-rose-300 mt-1">Error: {testResult.error}</div>}
              {testResult.recommendation && <div className="text-gray-400 mt-1 italic">{testResult.recommendation}</div>}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <ActionButton onClick={onClose} icon={X} label="Άκυρο" />
            <ActionButton onClick={save} icon={CheckCircle2} label="Αποθήκευση" busy={saving} variant="success" testId="save-urls" />
          </div>
        </div>
      )}
    </Modal>
  );
};

// ── Raw samples modal (coverage gap grid) ────────────────────
const SamplesModal = ({ station, onClose, token, dateFrom, dateTo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const q = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        const r = await fetch(`${API}/api/diagnostic/station-samples/${station.station_id}?${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    };
    load();
  }, [station.station_id, dateFrom, dateTo, token]);

  return (
    <Modal onClose={onClose} title={`Hourly samples coverage — ${station.station_name}`} width="max-w-4xl">
      {loading && <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Φόρτωση...</div>}
      {data && (
        <div className="space-y-3">
          <div className="flex items-center gap-4 text-xs">
            <span>Coverage: <span className={data.coverage_pct < 50 ? 'text-amber-300 font-bold' : 'text-emerald-300 font-bold'}>{data.coverage_pct}%</span></span>
            <span>Covered: {data.covered_hours} / {data.total_hours} ώρες</span>
            <span className="text-gray-500">{data.date_from} → {data.date_to}</span>
          </div>

          <div className="text-[11px] text-gray-400 mb-1">
            Grid: κάθε κελί = 1 ώρα. Γκρι = χωρίς δείγμα, χρωματιστό = με δείγματα (ένταση ∝ avg listeners)
          </div>

          {/* Grid by date */}
          {Array.from(new Set(data.samples.map(s => s.date))).map(date => {
            const hourly = data.samples.filter(s => s.date === date);
            const maxAvg = Math.max(1, ...hourly.filter(h => h.has_data).map(h => h.avg || 0));
            return (
              <div key={date} className="flex items-center gap-2">
                <div className="text-[10px] text-gray-500 w-20 font-mono">{date}</div>
                <div className="flex-1 flex gap-0.5">
                  {hourly.map(h => {
                    if (!h.has_data) {
                      return (
                        <div
                          key={h.hour}
                          className="flex-1 h-6 bg-gray-800/50 rounded-sm"
                          title={`${h.hour}:00 — χωρίς δείγμα`}
                        />
                      );
                    }
                    const intensity = Math.min(1, (h.avg || 0) / maxAvg);
                    const bg = `rgba(16, 185, 129, ${0.15 + intensity * 0.7})`;
                    return (
                      <div
                        key={h.hour}
                        className="flex-1 h-6 rounded-sm"
                        style={{ background: bg }}
                        title={`${h.hour}:00 — avg ${h.avg}, peak ${h.peak}, samples ${h.samples}`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex justify-between text-[9px] text-gray-500 pl-20 mt-1">
            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ── Math explanation modal ───────────────────────────────────
const ExplainMathModal = ({ station, onClose }) => {
  const raw_tlh = station.tlh.raw;
  const adj_tlh = station.tlh.adjusted;
  const base = station.scoring.base_audience;
  const conf = station.scoring.confidence ?? 1;
  const qual = station.scoring.quality_score ?? 1;
  const qapplied = station.scoring.quality_applied;

  return (
    <Modal onClose={onClose} title={`Υπολογισμός TLH — ${station.station_name}`}>
      <div className="space-y-3 text-sm">
        <div className="bg-gray-800/50 rounded p-3 font-mono text-xs text-gray-300 leading-relaxed">
          <div className="text-gray-500"># Βήμα 1: Raw TLH (πραγματικά samples)</div>
          <div>raw_avg_listeners × hours_with_data</div>
          <div>= {station.raw_listeners.avg} × {station.raw_listeners.hours_with_data}</div>
          <div className="text-emerald-300">= {raw_tlh.toFixed(2)} listener-hours</div>
        </div>

        <div className="bg-gray-800/50 rounded p-3 font-mono text-xs text-gray-300 leading-relaxed">
          <div className="text-gray-500"># Βήμα 2: Base audience (μετά Anchor Calibration)</div>
          <div>base_audience = {base != null ? Number(base).toFixed(2) : '—'}</div>
          {station.scoring.estimated_listeners != null && (
            <div className="text-gray-500">(estimated = {Number(station.scoring.estimated_listeners).toFixed(1)} — από calibration ratio όταν δεν έχει endpoint)</div>
          )}
        </div>

        <div className="bg-gray-800/50 rounded p-3 font-mono text-xs text-gray-300 leading-relaxed">
          <div className="text-gray-500"># Βήμα 3: Apply Tier confidence & Quality</div>
          <div>tier = {station.scoring.tier} → confidence = {Number(conf).toFixed(2)}</div>
          <div>quality_score = {Number(qual).toFixed(4)} {qapplied ? '(εφαρμόζεται)' : '(ΔΕΝ εφαρμόζεται)'}</div>
          <div>final_score = base × confidence {qapplied && '× quality'}</div>
          <div>            = {Number(base ?? 0).toFixed(2)} × {Number(conf).toFixed(2)} {qapplied && `× ${Number(qual).toFixed(3)}`}</div>
          <div className="text-blue-300">            = {Number(station.scoring.final_score ?? 0).toFixed(2)}</div>
        </div>

        <div className="bg-gray-800/50 rounded p-3 font-mono text-xs text-gray-300 leading-relaxed">
          <div className="text-gray-500"># Βήμα 4: Adjusted TLH (αυτό που χρησιμοποιείται στο ranking)</div>
          <div className="text-blue-300">adjusted_tlh = {adj_tlh.toFixed(2)}</div>
          <div className="text-gray-500 mt-1"># Σύγκριση με Raw</div>
          <div>delta = {adj_tlh.toFixed(2)} − {raw_tlh.toFixed(2)} = {(adj_tlh - raw_tlh).toFixed(2)}</div>
          <div className={Math.abs(station.tlh.delta_pct || 0) > 50 ? 'text-rose-300' : 'text-emerald-300'}>
            delta% = {station.tlh.delta_pct ?? '—'}%
          </div>
        </div>

        {Math.abs(station.tlh.delta_pct || 0) > 50 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200">
            <div className="font-semibold mb-1">Γιατί τόσο μεγάλη απόκλιση;</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Η κατάταξη δεν χρησιμοποιεί σκέτο raw TLH αλλά μετά Tier/Quality adjustment</li>
              <li>{qapplied ? 'Το quality_score εφαρμόστηκε (auto-loop penalty)' : 'Το confidence του tier αλλάζει την τελική τιμή'}</li>
              <li>Αν {raw_tlh === 0 ? 'δεν υπάρχουν real samples, η τιμή προέρχεται αποκλειστικά από plays × calibration ratio' : 'τα raw samples είναι λίγα (χαμηλή κάλυψη), το smoothing παίρνει μεγαλύτερο βάρος'}</li>
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ── Main Quick Fix toolbar ───────────────────────────────────
const QuickFixActions = ({ station, warning, token, onRefetch, dateFrom, dateTo }) => {
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null);
  const [inlineMsg, setInlineMsg] = useState(null);
  const actions = getActionsForWarning(warning);

  if (actions.length === 0) return null;

  const retest = async () => {
    setBusy('retest');
    setInlineMsg(null);
    try {
      // Fetch current station to get stats_url
      const r1 = await fetch(`${API}/api/stream-monitor/stations/${station.station_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const st = await r1.json();
      const url = st.stats_url || st.listeners_api_url;
      if (!url) { setInlineMsg({ type: 'err', text: 'Δεν υπάρχει stats_url/listeners_api_url' }); return; }
      const r = await fetch(`${API}/api/admin/test-listeners-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url }),
      });
      const d = await r.json();
      setInlineMsg({
        type: d.success ? 'ok' : 'err',
        text: d.success
          ? `✓ Endpoint ενεργό — ${d.listeners_detected} listeners`
          : `✗ ${d.error || 'Δεν επέστρεψε δεδομένα'} (HTTP ${d.raw_status_code || '?'})`,
      });
    } catch (e) {
      setInlineMsg({ type: 'err', text: String(e) });
    } finally { setBusy(null); }
  };

  const testUrl = async () => {
    setBusy('test_url');
    setInlineMsg(null);
    try {
      const r1 = await fetch(`${API}/api/stream-monitor/stations/${station.station_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const st = await r1.json();
      const url = st.stats_url || st.listeners_api_url;
      if (!url) {
        setModal('edit_urls'); // No URL → open edit modal directly
        return;
      }
      const r = await fetch(`${API}/api/admin/test-listeners-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url }),
      });
      const d = await r.json();
      setInlineMsg({
        type: d.success ? 'ok' : 'err',
        text: d.success ? `✓ Tested: ${d.listeners_detected} listeners` : `✗ ${d.error || 'fail'}`,
      });
    } finally { setBusy(null); }
  };

  const toggleActive = async () => {
    if (!window.confirm(`Toggle active/inactive για τον σταθμό "${station.station_name}";`)) return;
    setBusy('toggle');
    try {
      const r = await fetch(`${API}/api/stream-monitor/stations/${station.station_id}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        setInlineMsg({ type: 'ok', text: '✓ Κατάσταση άλλαξε' });
        onRefetch?.();
      } else setInlineMsg({ type: 'err', text: 'Αποτυχία' });
    } finally { setBusy(null); }
  };

  const openStream = async () => {
    try {
      const r = await fetch(`${API}/api/stream-monitor/stations/${station.station_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.stream_url) window.open(d.stream_url, '_blank', 'noopener');
      else setInlineMsg({ type: 'err', text: 'Χωρίς stream_url' });
    } catch {
      setInlineMsg({ type: 'err', text: 'Σφάλμα fetch' });
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-1 items-center">
        {actions.includes('retest') && (
          <ActionButton onClick={retest} icon={RefreshCw} label="Retest endpoint" busy={busy === 'retest'} variant="primary" testId={`qf-retest-${station.station_id}`} />
        )}
        {actions.includes('edit_urls') && (
          <ActionButton onClick={() => setModal('edit_urls')} icon={Settings} label="Ρυθμίσεις URLs" variant="primary" testId={`qf-edit-${station.station_id}`} />
        )}
        {actions.includes('test_url') && (
          <ActionButton onClick={testUrl} icon={Zap} label="Test sample" busy={busy === 'test_url'} testId={`qf-test-${station.station_id}`} />
        )}
        {actions.includes('open_stream') && (
          <ActionButton onClick={openStream} icon={ExternalLink} label="Άνοιγμα stream" testId={`qf-open-${station.station_id}`} />
        )}
        {actions.includes('toggle_active') && (
          <ActionButton onClick={toggleActive} icon={PauseCircle} label="Toggle active" busy={busy === 'toggle'} variant="danger" testId={`qf-toggle-${station.station_id}`} />
        )}
        {actions.includes('show_samples') && (
          <ActionButton onClick={() => setModal('samples')} icon={Grid3x3} label="Raw samples" testId={`qf-samples-${station.station_id}`} />
        )}
        {actions.includes('explain_math') && (
          <ActionButton onClick={() => setModal('math')} icon={Calculator} label="Γιατί;" testId={`qf-math-${station.station_id}`} />
        )}
      </div>

      {inlineMsg && (
        <div className={`mt-1 text-[10px] ${inlineMsg.type === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>
          {inlineMsg.text}
        </div>
      )}

      {modal === 'edit_urls' && (
        <EditUrlsModal
          station={station}
          token={token}
          onClose={() => setModal(null)}
          onSaved={() => { onRefetch?.(); }}
        />
      )}
      {modal === 'samples' && (
        <SamplesModal
          station={station}
          token={token}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'math' && (
        <ExplainMathModal station={station} onClose={() => setModal(null)} />
      )}
    </>
  );
};

export default QuickFixActions;
